import { useContext, useLayoutEffect, useMemo, useState } from "react";
import { StatusRow } from "./GroupGeneralLedgerTypeList";
import CustomizerContext from "../../../../../_helper/Customizer";
import useAuth from "../../../../../Hooks/useAuth";
import {
  Code,
  CreatedBy,
  Description,
  GroupGLClass,
  GroupGLType,
  ModifiedBy,
  GeneralLedgerGroup,
  Name,
  Status,
} from "../../../../../Constant";
import { createDropdownField } from "../../../../../Data/FormField/PublicData";
import { formatDateWithTimeZone } from "../../../../../_helper/dateHelper";
import {
  financesGeneralLedgerGroupApi,
  financesGroupGeneralLedgerClassApi,
  financesGroupGeneralLedgerTypeApi,
} from "../../../../../api";
import { IGroupGeneralLedgerTypeResponse } from "../GroupGeneralLedgerType.types";
import { toast } from "react-toastify";
import useAxiosPrivate from "../../../../../Hooks/useAxiosPrivate";

export default function useGroupGeneralLedgerTypeListController() {
  const axiosPrivate = useAxiosPrivate();
  const title = GroupGLType;
  const { userOrganisationId }: any = useAuth();
  const [formFields, setFormFields] = useState<any[]>([
    {
      id: "name",
      name: "name",
      label: Name,
      type: "text",
      value: "",
      required: true,
      colSize: 6,
    },

    {
      id: "description",
      name: "description",
      label: Description,
      type: "textarea",
      value: "",
      required: true,
      colSize: 6,
    },

    {
      id: "is_active",
      name: "is_active",
      label: Status,
      type: "switch",
      value: "1",
      colSize: 6,
    },
  ]);

  const groupGLClassIdDropDown = createDropdownField(
    axiosPrivate,
    "group_gl_class_id",
    "group_gl_class_id",
    GroupGLClass,
    `${financesGroupGeneralLedgerClassApi}?organisation_id=${userOrganisationId}&gl_group_id=${1}`,
    "",
    6
  );
  groupGLClassIdDropDown.onChange = async (id, fields) => {
    const updatedFormFields = fields.map((field: any) => {
      if (field.id === "group_gl_class_id") {
        return { ...field, value: id };
      }
      return field;
    });
    setFormFields([...updatedFormFields]);
  };

  const tableColumns = [
    {
      name: Name,
      selector: (row: (typeof data)[0]) => `${row.name}`,
      sortable: true,
      center: false,
    },

    {
      name: Code,
      selector: (row: (typeof data)[0]) => `${row.code}`,
      sortable: true,
      center: true,
    },

    {
      name: GroupGLClass,
      selector: (row: (typeof data)[0]) => `${row.group_gl_class.name}`,
      sortable: true,
      center: false,
    },
    {
      name: Status,
      selector: (row: (typeof data)[0]) => `${row.is_active}`,
      sortable: true,
      center: true,
      cell: (row: (typeof data)[0]) => StatusRow(row),
    },

    {
      name: CreatedBy,
      selector: (row: (typeof data)[0]) =>
        `${formatDateWithTimeZone(row.created_at)}`,
      sortable: false,
      center: false,
    },

    {
      name: ModifiedBy,
      selector: (row: (typeof data)[0]) =>
        `${formatDateWithTimeZone(row.updated_at)}`,
      sortable: false,
      center: false,
    },
  ];

  const generalLedgerGroup = createDropdownField(
    axiosPrivate,
    "general_ledger_group",
    "general_ledger_group",
    GeneralLedgerGroup,
    `${financesGeneralLedgerGroupApi}?&organisation_id=${userOrganisationId}&group_gl_class_id=${1}`,
    "",
    6
  );
  generalLedgerGroup.onChange = async (id, updatedFields) => {
    const nextField = updatedFields.find(
      (field: any) => field.id === "group_gl_class_id"
    );

    if (nextField) {
      const endPoint = `${financesGroupGeneralLedgerClassApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}&gl_group_id=${id}`;

      await nextField.fetchOptions(id, false, endPoint);
      const updatedFormFields = updatedFields.map((field: any) => {
        if (field.id === "general_ledger_group") {
          return { ...field, value: id };
        }
        return field;
      });
      setFormFields([...updatedFormFields]);
    }
  };

  useLayoutEffect(() => {
    //?? Updates all fields once component is mounted
    (async function () {
      const updatedFields = [
        generalLedgerGroup,
        groupGLClassIdDropDown,
        ...formFields,
      ];
      await generalLedgerGroup.fetchOptions();
      setFormFields(updatedFields);
    })();
  }, []);

  const fetchData = () => {
    setLoading(true);
    axiosPrivate
      .get(
        `${financesGroupGeneralLedgerTypeApi}?skip=${0}&limit=${10}&with_trashed=${false}&organisation_id=${userOrganisationId}&gl_group_id=${1}`
      )
      .then((res) => {
        const response: IGroupGeneralLedgerTypeResponse[] = res.data;
        setData(response);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          toast.error(err.response.data.detail);
        } else {
          toast.error('Something Went Wrong');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useLayoutEffect(() => {
    fetchData();
  }, []);

  const { layoutURL } = useContext(CustomizerContext);
  const newBtn = `${process.env.PUBLIC_URL}/finance/settings/group-general-ledger-type/create/${layoutURL}`;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IGroupGeneralLedgerTypeResponse[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<
    (typeof data)[0] | null
  >(null);
  const [editMode, setEditMode] = useState(false);

  const handleEdit = (row: (typeof data)[0]) => {
    setViewModalOpen(true);
    setEditMode(true);
    groupGLClassIdDropDown.onChange(row.group_gl_class.id, formFields);
    setSelectedRowData(row);
    console.log(row);
  };

  const handleView = (row: (typeof data)[0]) => {
    setViewModalOpen(true);
    setEditMode(false);
    setSelectedRowData(row);
  };

  const handleEditRec = async (
    id: number | string,
    editRecord: (typeof data)[0]
  ) => {
    console.log(id, editRecord, "");
    await axiosPrivate
      .put(`${financesGroupGeneralLedgerTypeApi}/${id}`, {
        ...editRecord,
        organisation_id: userOrganisationId,
      })
      .then((_res) => {
        const updatedData = data.map((row) =>
          row.id === selectedRowData!.id ? { ...row, ..._res.data } : row
        );
        setData(updatedData);
        toast.success(`${_res.data.name}, 'Edited Successfully'`);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          toast.warn("Group GL Type not found");
        }
        toast.error(err.response.data.message || "Whoops! Something went wrong");
      });
  };

  const memoizedHandleEdit = useMemo(
    () => handleEditRec,
    [data, selectedRowData]
  );

  return {
    data,
    tableColumns,
    viewModalOpen,
    selectedRowData,
    editMode,
    newBtn,
    title,
    formFields,
    loading,
    setViewModalOpen,
    handleView,
    handleEdit,
    memoizedHandleEdit,
  };
}
